import type { RequirementStatus } from '../api/requirements';

export type PipelineSegment = 'idle' | 'done' | 'active' | 'err';

export type PipelineState = {
  readonly segments: readonly [PipelineSegment, PipelineSegment, PipelineSegment, PipelineSegment];
  readonly label: string;
};

const PIPELINE: Record<RequirementStatus, PipelineState> = {
  INTAKE: { segments: ['active', 'idle', 'idle', 'idle'], label: 'Eingang' },
  TRIAGE: { segments: ['done', 'active', 'idle', 'idle'], label: 'Klassifikation prüfen' },
  REFINING: { segments: ['done', 'done', 'active', 'idle'], label: 'Aufbereitung läuft' },
  CLARIFICATION: { segments: ['done', 'done', 'err', 'idle'], label: 'Rückfrage offen' },
  READY_FOR_RELEASE: { segments: ['done', 'done', 'done', 'active'], label: 'Finale prüfen' },
  RELEASED: { segments: ['done', 'done', 'done', 'done'], label: 'Freigegeben' },
  ARCHIVED: { segments: ['done', 'done', 'done', 'done'], label: 'Archiviert' },
};

const LEGACY_ALIAS: Record<string, RequirementStatus> = {
  NEW: 'INTAKE',
  AWAITING_INFO: 'CLARIFICATION',
  CLASSIFICATION_APPROVED: 'REFINING',
  APPROVED: 'RELEASED',
  IN_REVIEW: 'TRIAGE',
  IN_REVIEW_1: 'TRIAGE',
  IN_REVIEW_2: 'READY_FOR_RELEASE',
};

const UNKNOWN_PIPELINE: PipelineState = {
  segments: ['idle', 'idle', 'idle', 'idle'],
  label: 'Unbekannt',
};

const warned = new Set<string>();

export function normaliseStatus(status: unknown): RequirementStatus | null {
  if (typeof status !== 'string') return null;
  if (status in PIPELINE) return status as RequirementStatus;
  const aliased = LEGACY_ALIAS[status];
  if (aliased) return aliased;
  return null;
}

function resolve(status: RequirementStatus): PipelineState {
  const direct = PIPELINE[status];
  if (direct) return direct;
  const aliased = LEGACY_ALIAS[status as unknown as string];
  if (aliased) return PIPELINE[aliased];
  if (!warned.has(String(status))) {
    warned.add(String(status));
    console.warn(`[mindwright] unknown requirement status "${String(status)}" — falling back to neutral state.`);
  }
  return UNKNOWN_PIPELINE;
}

export function pipelineFor(status: RequirementStatus): PipelineState {
  return resolve(status);
}

export function statusLabel(status: RequirementStatus): string {
  return resolve(status).label;
}

export type RailStepKey = 'INTAKE' | 'CLASSIFY' | 'GATE_1' | 'REFINED' | 'GATE_2';
export type RailStepState = 'idle' | 'done' | 'active' | 'err';

export type RailStep = {
  readonly key: RailStepKey;
  readonly label: string;
  readonly state: RailStepState;
};

const RAIL_LABELS: Record<RailStepKey, string> = {
  INTAKE: 'Eingang',
  CLASSIFY: 'KI-Klass.',
  GATE_1: 'Gate 1',
  REFINED: 'Aufbereitet',
  GATE_2: 'Gate 2',
};

export function railStepsFor(status: RequirementStatus): readonly RailStep[] {
  const order: readonly RailStepKey[] = ['INTAKE', 'CLASSIFY', 'GATE_1', 'REFINED', 'GATE_2'];
  const stateMap = railStateMap(normaliseStatus(status) ?? 'INTAKE');
  return order.map((key) => ({
    key,
    label: RAIL_LABELS[key],
    state: stateMap[key],
  }));
}

function railStateMap(status: RequirementStatus): Record<RailStepKey, RailStepState> {
  switch (status) {
    case 'INTAKE':
      return { INTAKE: 'active', CLASSIFY: 'idle', GATE_1: 'idle', REFINED: 'idle', GATE_2: 'idle' };
    case 'TRIAGE':
      return { INTAKE: 'done', CLASSIFY: 'done', GATE_1: 'active', REFINED: 'idle', GATE_2: 'idle' };
    case 'REFINING':
      return { INTAKE: 'done', CLASSIFY: 'done', GATE_1: 'done', REFINED: 'active', GATE_2: 'idle' };
    case 'CLARIFICATION':
      return { INTAKE: 'done', CLASSIFY: 'done', GATE_1: 'done', REFINED: 'err', GATE_2: 'idle' };
    case 'READY_FOR_RELEASE':
      return { INTAKE: 'done', CLASSIFY: 'done', GATE_1: 'done', REFINED: 'done', GATE_2: 'active' };
    case 'RELEASED':
    case 'ARCHIVED':
      return { INTAKE: 'done', CLASSIFY: 'done', GATE_1: 'done', REFINED: 'done', GATE_2: 'done' };
  }
}

export function nextActionHint(status: RequirementStatus): string | null {
  const canonical = normaliseStatus(status);
  if (!canonical) return null;
  switch (canonical) {
    case 'INTAKE':
      return 'KI klassifiziert …';
    case 'TRIAGE':
      return 'Klassifikation freigeben';
    case 'REFINING':
      return 'KI bereitet auf …';
    case 'CLARIFICATION':
      return 'Rückfragen klären';
    case 'READY_FOR_RELEASE':
      return 'Finale Freigabe';
    case 'RELEASED':
      return 'Markdown verfügbar';
    case 'ARCHIVED':
      return null;
  }
}
